import * as React from 'react';
import { Button as PrivateButton, ButtonProps as PrivateButtonProps } from '@chakra-ui/core';
import { BoxProps } from '../box';

interface ButtonProps extends BoxProps {
    variant?: 'solid' | 'outline' | 'outline-fill' | 'ghost' | 'link';
    variantColor?: 'default' | 'light' | 'dark' | 'brand' | 'destructive';
    isDisabled?: boolean;
    isLoading?: boolean;
    loadingText?: string;
    size?: string;
}

const colorVariants = {
    default: {
        bg: 'bg.reversed.primary',
        borderColor: 'bg.reversed.primary',
        color: 'text.reversed.primary',
        hover: {
            bg: 'bg.reversed.secondary',
            bgLight: '',
            borderColor: 'bg.reversed.secondary'
        },
        active: {
            bg: 'bg.reversed.tertiary',
            bgLight: '',
            borderColor: 'bg.reversed.tertiary'
        }
    },
    brand: {
        bg: 'brand.green.base',
        borderColor: 'brand.green.base',
        color: 'brand.white.base',
        hover: {
            bg: 'brand.green.400',
            bgLight: 'brand.green.200',
            borderColor: 'brand.green.400'
        },
        active: {
            bg: 'brand.green.600',
            bgLight: 'brand.green.400',
            borderColor: 'brand.green.600'
        }
    },
    'brand-blue': {
        bg: 'brand.lightBlue.base',
        borderColor: 'brand.green.lightBlue',
        color: 'brand.white.base',
        hover: {
            bg: 'brand.lightBlue.400',
            bgLight: 'brand.lightBlue.200',
            borderColor: 'brand.lightBlue.400'
        },
        active: {
            bg: 'brand.lightBlue.600',
            bgLight: 'brand.lightBlue.400',
            borderColor: 'brand.lightBlue.600'
        }
    },
    dark: {
        bg: 'brand.gray.1200',
        borderColor: 'brand.gray.1200',
        color: 'brand.white.base',
        hover: {
            bg: 'brand.gray.900',
            bgLight: 'brand.gray.200',
            borderColor: 'brand.gray.900'
        },
        active: {
            bg: 'brand.gray.1100',
            bgLight: 'brand.gray.300',
            borderColor: 'brand.gray.1100'
        }
    },
    light: {
        bg: 'brand.white.base',
        borderColor: 'brand.white.base',
        color: 'brand.gray.base',
        hover: {
            bg: 'brand.gray.200',
            bgLight: 'brand.gray.200',
            borderColor: 'brand.gray.200'
        },
        active: {
            bg: 'brand.gray.300',
            bgLight: 'brand.gray.300',
            borderColor: 'brand.gray.300'
        }
    },
    "light-darkTheme": {
        bg: 'brand.white.base',
        borderColor: 'brand.white.base',
        color: 'brand.gray.base',
        hover: {
            bg: 'brand.gray.800',
            bgLight: 'brand.gray.800',
            borderColor: 'brand.gray.800'
        },
        active: {
            bg: 'brand.gray.700',
            bgLight: 'brand.gray.700',
            borderColor: 'brand.gray.700'
        }
    },
    destructive: {
        bg: 'red.600',
        borderColor: 'red.600',
        color: 'brand.white.base',
        hover: {
            bg: 'red.500',
            bgLight: 'red.300',
            borderColor: 'red.500'
        },
        active: {
            bg: 'red.700',
            bgLight: 'red.500',
            borderColor: 'red.700'
        }
    }
};

const variantProps = (variant: string, color: string): any => {
    if (!colorVariants[color]) return {};

    switch (variant) {
    case 'solid':
        return {
            bg: colorVariants[color].bg,
            border: '2px solid transparent',
            color: colorVariants[color].color,
            _hover: {
                bg: colorVariants[color].hover.bg
            },
            _active: {
                bg: colorVariants[color].active.bg
            }
        };
    case 'ghost':
        return {
            bg: 'transparent',
            border: '2px solid transparent',
            color: colorVariants[color].borderColor,
            _hover: {
                bg: colorVariants[color].hover.bgLight
            },
            _active: {
                bg: colorVariants[color].active.bgLight
            }
        };
    case 'outline':
    case 'outline-fill':
        return {
            bg: variant === 'outline-fill' ? 'bg.base.primary' : 'transparent',
            borderColor: colorVariants[color].borderColor,
            borderStyle: 'solid',
            borderWidth: '2px',
            color: colorVariants[color].borderColor,
            _hover: {
                bg: variant === 'outline-fill' ? 'auto' : 'transparent',
                borderColor: colorVariants[color].hover.borderColor
            },
            _active: {
                bg: variant === 'outline-fill' ? 'auto' : 'transparent',
                borderColor: colorVariants[color].active.borderColor
            }
        };
    case 'link':
        return {
            bg: 'transparent',
            border: '2px solid transparent',
            color: colorVariants[color].borderColor,
            _hover: {
                bg: 'transparent',
                color: colorVariants[color].hover.borderColor
            },
            _active: {
                bg: 'transparent',
                color: colorVariants[color].active.borderColor
            }
        };
    default:
        return {};
    }
};

const Button: any = React.forwardRef(
    ({ size = 'md', variant = 'solid', variantColor = 'brand', ...rest }: ButtonProps, ref: any) => {
        let height = 24;

        const styles: any = {
            fontFamily: 'button',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: 'widest',
            rounded: 'full',
            transition: 'all 150ms',
            lineHeight: 'normal',
            cursor: 'pointer'
        };

        if (!rest.fontSize) {
            switch (size) {
            case 'lg':
                styles.fontSize = 'md';
                break;
            case 'md':
                styles.fontSize = 'sm';
                break;
            case 'sm':
                styles.fontSize = 'xs';
                break;
            case 'xs':
                styles.fontSize = '2xs';
                break;
            default:
                styles.fontSize = 'sm';
            }
        }

        switch (size) {
        case 'lg':
            styles.px = 5;
            height = 48;
            break;
        case 'md':
            styles.px = 4;
            height = 40;
            break;
        case 'sm':
            styles.px = 3;
            height = 32;
            break;
        case 'xs':
            styles.px = '12px';
            height = 24;
            break;
        default:
            break;
        }

        const variantStyle = variantProps(variant, variantColor);
        styles.height = `${height / 16}rem`;

        return <PrivateButton ref={ref} {...variantStyle} {...styles} {...rest} />;
    }
);

export { Button, ButtonProps };
